'use client';
// ReviewSlider — THE review slider (M3, Founder 02/10/26: "כל ההמלצות סליידר, והמשתמש מבין שזה סליידר כי רואים את הקצה של הביקורת
// הבאה"). ONE door for every public review list: the control card (ReviewsList), the pro page (ReviewsV2More), the 3.x renderers.
// CSS lives once in app/globals.css (.rv-slider / .rv-slide): scroll-snap x mandatory, RTL (first review on the right), the next
// review's edge peeks ~14% of a 390px screen, no visible scrollbar. Mouse drag = lib/useDragScroll; touch = native.
// Analytics: one 'review_swipe' per slider per mount, on the first horizontal scroll past 24px.
import { useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { trackEvent } from '@/components/Tracker';
import { useDragScroll } from '@/lib/useDragScroll';

export const REVIEW_SLIDER_LABEL = 'המלצות – גלילה לצדדים';

export default function ReviewSlider({ proId, children, bleed = false, className = '', style, scrollRef, label = REVIEW_SLIDER_LABEL }: {
  proId: number; children: ReactNode;
  /** the scroller runs edge-to-edge through a 16px-padded parent (margin -16 / padding 16) */
  bleed?: boolean; className?: string; style?: CSSProperties;
  /** a caller that needs the scroller element itself (ReviewsList's lazy-load IntersectionObserver root) */
  scrollRef?: RefObject<HTMLDivElement | null>; label?: string;
}) {
  const own = useRef<HTMLDivElement | null>(null);
  const ref = scrollRef ?? own;
  useDragScroll(ref);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    let fired = false;
    const on = () => {
      if (fired || Math.abs(el.scrollLeft) <= 24) return; // RTL scrollLeft runs 0 → negative
      fired = true; el.removeEventListener('scroll', on);
      const first = el.querySelector<HTMLElement>('.rv-slide');
      const step = first ? first.getBoundingClientRect().width + 10 : el.clientWidth;
      trackEvent('review_swipe', { metadata: { proId, idx: Math.round(Math.abs(el.scrollLeft) / Math.max(1, step)) }, professionalId: proId });
    };
    el.addEventListener('scroll', on, { passive: true });
    return () => el.removeEventListener('scroll', on);
  }, [proId, ref]);
  return (
    <div ref={ref} tabIndex={0} aria-label={label} data-review-slider className={`rv-slider${bleed ? ' rv-bleed' : ''}${className ? ` ${className}` : ''}`} style={style}>
      {children}
    </div>
  );
}

/** one review = one slide */
export function ReviewSlide({ children, className = '', style }: { children: ReactNode; className?: string; style?: CSSProperties }) {
  return <div role="group" aria-roledescription="slide" className={`rv-slide${className ? ` ${className}` : ''}`} style={style}>{children}</div>;
}

/** a long review inside a slide: clamped to `lines` with a "קרא עוד" toggle (the full text stays in the DOM — SEO + one tap away),
 *  so one long review never stretches the whole slider row */
export function ReviewSlideText({ children, lines = 7, className, style }: { children: ReactNode; lines?: number; className?: string; style?: CSSProperties }) {
  const ref = useRef<HTMLParagraphElement | null>(null);
  const [open, setOpen] = useState(false);
  const [over, setOver] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el || open) return;
    const fit = () => setOver(el.scrollHeight > el.clientHeight + 2);
    fit(); const ro = new ResizeObserver(fit); ro.observe(el); return () => ro.disconnect();
  }, [open]);
  return (
    <>
      <p ref={ref} className={className} style={{ ...style, ...(!open ? { display: '-webkit-box', WebkitLineClamp: lines, WebkitBoxOrient: 'vertical' as const, overflow: 'hidden' } : {}) }}>{children}</p>
      {(over || open) && (
        <button type="button" onClick={() => { if (!open) trackEvent('review_expand'); setOpen(v => !v); }} className="text-xs font-bold" style={{ color: '#1d4ed8', background: 'none', border: 'none', padding: '8px 0', margin: '-4px 0', cursor: 'pointer', fontFamily: 'inherit' }}>
          {open ? 'הצג פחות' : 'קרא עוד'}
        </button>
      )}
    </>
  );
}
